import React, { useCallback, useEffect, useState } from "react";
import { DashboardLayout } from "./DashboardLayout";
import { api, MXN, formatDateMX } from "../../lib/apiClient";
import { toast } from "sonner";
import { Search, X, Save } from "lucide-react";
import { useAdminRealtime } from "../../hooks/useAdminRealtime";

export default function Clientes() {
  const [list, setList] = useState([]);
  const [search, setSearch] = useState("");
  const [sel, setSel] = useState(null);

  const load = useCallback(() => api.get("/customers", { params: search ? { search } : {} }).then((r) => setList(r.data)).catch(() => {}), [search]);
  useEffect(() => { load(); }, [load]);
  useAdminRealtime(load, ["orders"]);

  const openCustomer = (id) => api.get(`/customers/${id}`).then((r) => setSel(r.data));

  const save = async () => {
    await api.patch(`/customers/${sel.id}`, { name: sel.name, phone: sel.phone, email: sel.email, address: sel.address, notes: sel.notes });
    toast.success("Cliente actualizado"); setSel(null); load();
  };

  return (
    <DashboardLayout title="Clientes">
      <div className="relative w-full max-w-md mb-5">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-rosa" />
        <input data-testid="clientes-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar por nombre o teléfono…"
          className="w-full bg-blancoCalido border-2 border-beige rounded-full pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:border-vino" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {list.map((c) => (
          <button key={c.id} onClick={() => openCustomer(c.id)} data-testid={`cliente-${c.id}`} className="w-full min-w-0 text-left bg-blancoCalido border border-beige rounded-[22px] p-5 shadow-pastel hover:-translate-y-1 transition-all">
            <p className="font-serif text-xl text-vino">{c.name || "Sin nombre"}</p>
            <p className="text-sm text-cafe/60">{c.phone}</p>
            <div className="flex gap-3 mt-3 text-xs text-cafe/60">
              <span>{c.orders_count} pedido(s)</span>
              <span>·</span>
              <span>{MXN(c.total_spent)}</span>
            </div>
            {c.last_order && <p className="text-xs text-cafe/40 mt-1">Último: {formatDateMX(c.last_order)}</p>}
          </button>
        ))}
      </div>
      {list.length === 0 && <p className="text-cafe/50 text-sm">Aún no hay clientes.</p>}

      {sel && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="cliente-drawer">
          <div className="absolute inset-0 bg-vinoDark/30 backdrop-blur-sm" onClick={() => setSel(null)} />
          <div className="relative w-full max-w-md bg-crema h-full overflow-y-auto overflow-x-hidden p-4 sm:p-6 pb-[calc(6rem+env(safe-area-inset-bottom))] sm:pb-6">
            <div className="flex justify-between items-center gap-3 mb-4"><h2 className="font-serif text-2xl text-vino truncate">{sel.name}</h2><button onClick={() => setSel(null)} className="text-vino p-2 shrink-0"><X /></button></div>
            <div className="space-y-3 bg-blancoCalido border border-beige rounded-[20px] p-4">
              {[["Nombre", "name"], ["Teléfono", "phone"], ["Correo", "email"], ["Dirección", "address"]].map(([l, k]) => (
                <div key={k}><label className="text-xs text-cafe/60">{l}</label>
                  <input data-testid={`cust-edit-${k}`} value={sel[k] || ""} onChange={(e) => setSel({ ...sel, [k]: e.target.value })} className="w-full bg-crema border-2 border-beige rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-vino" /></div>
              ))}
              <div><label className="text-xs text-cafe/60">Notas internas</label>
                <textarea value={sel.notes || ""} onChange={(e) => setSel({ ...sel, notes: e.target.value })} rows={2} className="w-full bg-crema border-2 border-beige rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-vino" /></div>
              <button onClick={save} data-testid="cust-save" className="w-full justify-center bg-vino text-crema rounded-full px-5 py-2.5 text-sm flex items-center gap-2"><Save className="w-4 h-4" /> Guardar</button>
            </div>
            <p className="text-sm text-cafe/60 mt-4 mb-2">Total comprado: <b className="text-vino">{MXN(sel.total_spent)}</b></p>
            <h3 className="font-medium text-vino mb-2">Historial de pedidos</h3>
            <div className="space-y-2">
              {(sel.orders || []).map((o) => (
                <div key={o.id} className="bg-blancoCalido border border-beige rounded-xl p-3 text-sm">
                  <div className="flex justify-between gap-2"><span className="font-medium text-vino truncate">{o.folio}</span><span className="text-cafe/60 shrink-0">{formatDateMX(o.delivery_date)}</span></div>
                  <p className="text-cafe/70">{o.product_name} · {MXN(o.final_price)}</p>
                </div>
              ))}
              {(!sel.orders || sel.orders.length === 0) && <p className="text-cafe/50 text-sm">Sin pedidos.</p>}
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
